import { Axios } from "api/axios";
import { ItemType } from "assets/types/itemTypes";
import { LoadingCard } from "Components/LoadingCard/LoadingCard";
import { QuantityInput } from "Components/Other/quantityInput";
import { useFormik } from "formik";
import { calculateItemValues } from "helpers/functions/calculateItemValues";
import { formatLabel } from "helpers/functions/formatLabel";
import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import Accordion from "react-bootstrap/Accordion";
import { toast } from "react-toastify";
import * as Yup from "yup";
const DynamicItems = lazy(() => import("Components/DynamicComp/DynamicItems"));

// Actions column background color
const ACTIONS_COLUMN_BG_COLOR = "#e3f2fd";

type AttributesTypes = {
  id: string;
  name: string;
};

type Category = {
  category_id: string;
  category_name: string;
  is_mandatory: boolean;
  attributes: AttributesTypes[];
};

export const RESTRICTED_SINGLE_ITEM_CATEGORIES = [
  { category_id: "743cffc9-09ae-4509-be91-314361e7bb33", category_name: "CPU" },
  // {
  //   category_id: "54f1c6b9-481a-4a16-a70e-bfe49341adf8",
  //   category_name: "Memory",
  // },
  {
    category_id: "14872373-f2f1-45ed-802e-81ef0971d0c6",
    category_name: "Motherboard",
  },
  // {
  //   category_id: "462c9208-9d11-42a3-868c-6b85ceebdb01",
  //   category_name: "Graphic Card",
  // },
  {
    category_id: "e2ec7862-300c-4dcc-9fdb-650e5ddd8180",
    category_name: "Cabinet",
  },
  {
    category_id: "a725335d-fef3-489c-ac5d-e3d609e77e29",
    category_name: "CPU Cooler",
  },
  // {
  //   category_id: "18b90cd7-8616-4e0f-b009-cd9ab544b9f5",
  //   category_name: "Cooling Fans",
  // },
  {
    category_id: "ccc2426f-df6b-4e6a-b14a-ce7c29918126",
    category_name: "SMPS",
  },
];

interface Dependencies {
  cpu_motherboard: string;
  motherboard_memory: string;
  // motherboard_cabinet: string;
}

type SelectedRow = ItemType & {
  tax_rate: number; // equals gst but editable
  discount: number; // numeric field
  discount_type: "%" | "flat";
  taxable_value: number; // selling price - discount
  tax_amount: number; // total tax on item
  item_amount: number; // final price (after discount + tax)
  original_selling_price?: number; // original selling price from database (for validation)
  brand_name?: string;
  sr_no?: number; // serial number for non-mandatory categories only
};

const BuildYourPC = ({
  itemList,
  setItemList,
  isEdit,
  onValidationChange,
}: any) => {
  document.title = "Build Your PC";

  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);
  const [selected, setSelected] = useState<SelectedRow[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const [categories, setCategories] = useState<Category[]>([]);
  const [ITEM_DETAIL_KEYS, setItemKeys] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<{
    template_name?: string;
    description?: string;
    keywords?: string[];
    item_details?: Record<string, any[]>;
  }>({});

  const [dependencies, setDependencies] = useState({
    cpu_motherboard: "",
    motherboard_memory: "",
    // motherboard_cabinet: "",
  });

  // Fetch All the categories to show rows
  useEffect(() => {
    // After fetching categories
    const fetchCategoriesList = async () => {
      setCategoriesLoading(true);
      try {
        let response = await Axios.get("/api/category/category-dropdown/");
        const updatedCat: Category[] = response.data.data.map((cat: any) => ({
          ...cat,
        }));

        setCategories(updatedCat);

        // Dynamically create ITEM_DETAIL_KEYS
        const KEYS = updatedCat.map(
          (cat) =>
            cat.category_name
              .toLowerCase() // lowercase
              .replace(/\s+/g, "_") // replace spaces with underscores
              .replace(/[^a-z0-9_]/g, "") // remove special characters if needed
        );

        setItemKeys(KEYS);

        console.log("ITEM_DETAIL_KEYS", ITEM_DETAIL_KEYS);
      } catch (error) {
        console.error("Failed to fetch categories", error);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategoriesList();
  }, []);

  // Populate selectedItems in edit mode and update dependencies
  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      if (!itemList) {
        setIsLoading(false);
        return;
      }
      const selectedItems: SelectedRow[] = [];
      const newDependencies = {
        cpu_motherboard: "",
        motherboard_memory: "",
        // motherboard_cabinet: "",
      };

      Object.keys(itemList).forEach((key) => {
        itemList[key].forEach((item: any) => {
          selectedItems.push(
            calculateItemValues({
              ...item,
              quantity: item.quantity || 1,
              tax_rate: Number(item.gst),
              gst: Number(item.gst),
              discount: item.discount ?? 0,
              discount_type: item.discount_type ?? "%",
              original_selling_price:
                item.original_selling_price ?? item.selling_price, // Store original price
              brand: item.brand, // Preserve brand ID
              brand_name: item.brand_name, // Preserve brand name
              sr_no: item.sr_no ?? undefined, // Preserve sr_no from backend (for non-mandatory categories)
            })
          );

          // Update dependencies based on CPU and Motherboard items
          if (item.category === "743cffc9-09ae-4509-be91-314361e7bb33") {
            // CPU
            const socketAttr = item.attributes?.find((a: any) =>
              [
                "Processor-socket-type",
                "processor_socket_type",
                "Processor Socket Type",
              ].includes(a.attribute_name)
            );
            if (socketAttr) {
              newDependencies.cpu_motherboard = socketAttr.manage_value_name;
            }
          }

          if (item.category === "14872373-f2f1-45ed-802e-81ef0971d0c6") {
            // Motherboard
            // const formFactorAttr = item.attributes?.find((a: any) =>
            //   ["Motherboard form factor", "motherboard_form_factor", "Motherboard Form Factor"].includes(
            //     a.attribute_name
            //   )
            // );
            const ramTypeAttr = item.attributes?.find((a: any) =>
              ["Ram Type", "ram_type"].includes(a.attribute_name)
            );
            // if (formFactorAttr) {
            //   newDependencies.motherboard_cabinet =
            //     formFactorAttr.manage_value_name;
            // }
            if (ramTypeAttr) {
              newDependencies.motherboard_memory =
                ramTypeAttr.manage_value_name;
            }
          }
        });
      });

      setSelected((prev) =>
        JSON.stringify(prev) === JSON.stringify(selectedItems)
          ? prev
          : selectedItems
      );
      setDependencies(newDependencies);

      console.log(selectedItems);
      console.log(itemList);
      setIsLoading(false);
    }, 2000);
  }, []);

  const formik = useFormik({
    initialValues: {
      template_name: initialData.template_name || "",
      description: initialData.description || "",
      keywords: initialData.keywords || [],
    },
    enableReinitialize: true,
    // enableReinitialize: true,
    validationSchema: Yup.object({
      template_name: Yup.string()
        .required("Please enter template name")
        .test("template-toast", "Please enter template name", function (value) {
          if (!value || !value.trim()) {
            toast.error("Please enter template name");
            return false;
          }
          return true;
        }),
      description: Yup.string().nullable(),
      keywords: Yup.array()
        .of(Yup.string())
        .test(
          "keywords-toast",
          "Please add at least one keyword",
          function (val) {
            if (!val || val.length === 0) {
              toast.error("Please add at least one keyword");
              return false;
            }
            return true;
          }
        ),
    }),
    onSubmit: async (values) => {
      // console.log(values);
      // // Check for selected items
      // if (selected.length === 0) {
      //   toast.error("Please add at least one item to the template");
      //   return;
      // }
      // const item_details: Record<string, any[]> = {};
      // ITEM_DETAIL_KEYS.forEach((key) => (item_details[key] = []));
      // // Map selected UI items to API shape
      // selected.forEach((it) => {
      //   const payloadRow = {
      //     item_id: it.item_id,
      //     category: it.category,
      //     category_name: it.category_name,
      //     item_name: it.item_name,
      //     item_sku: it.item_sku,
      //     selling_price: Number(it.selling_price ?? 0).toFixed(2),
      //     hsn_code: null,
      //     hsn_name: null,
      //     gst: Number(it.gst ?? 0).toFixed(2),
      //     tax_rate: it.tax_rate, // add tax_rate
      //     discount: it.discount, // add discount
      //     discount_type: it.discount_type, // add discount_type
      //     taxable_value: it.taxable_value, // add taxable_value
      //     tax_amount: it.tax_amount, // add tax_amount
      //     item_amount: it.item_amount, // add final amount
      //     quantity: it.quantity,
      //     remarks: "",
      //     tags: null,
      //     tag_name: null,
      //     image: null,
      //     attributes: it.attributes,
      //   };
      //   if (!item_details[it.category]) {
      //     item_details[it.category_name] = [];
      //   }
      //   item_details[it.category_name].push(payloadRow);
      // });
      // // const payload = {
      // //   ...values,
      // //   template_name: values.template_name,
      // //   description: values.description,
      // //   keywords: values.keywords,
      // //   item_details,
      // //   template_id: id, // Add template_id for update
      // // };
      // // console.log(item_details);
      // setItemList(item_details);
    },
  });

  // console.log(itemList)

  const addItem = (categoryId: string, item: ItemType) => {
    setSelected((prev: any) => {
      const exists = prev.find(
        (p: any) => p.item_id === item.item_id && p.category === categoryId
      );

      if (
        RESTRICTED_SINGLE_ITEM_CATEGORIES.some(
          (c) => c.category_id === categoryId
        )
      ) {
        const alreadyExists = prev.some((p: any) => p.category === categoryId);
        if (alreadyExists) {
          const categoryName = RESTRICTED_SINGLE_ITEM_CATEGORIES.find(
            (c) => c.category_id === categoryId
          )?.category_name;
          toast.error(`Only one ${categoryName} can be added`);
          return prev;
        }
        // Auto close accordion for restricted categories after selection
        setOpenCategoryId(null);
      }

      // Auto close accordion for all categories after selection
      setOpenCategoryId(null);

      if (exists) {
        return prev.map((p: any) =>
          p.item_id === item.item_id &&
          p.category === categoryId &&
          p.quantity < 10000
            ? calculateItemValues({
                ...p,
                quantity: Math.min(10000, (p.quantity || 1) + 1),
              })
            : p
        );
      }

      // Check if category is non-mandatory (optional) - only assign sr_no for non-mandatory categories
      const category = categories.find((c) => c.category_id === categoryId);
      const isNonMandatory = category && !category.is_mandatory;

      // Calculate next sr_no for non-mandatory categories only
      let sr_no: number | undefined = undefined;
      if (isNonMandatory) {
        // Find max sr_no within items of the same non-mandatory category
        const categoryItems = prev.filter((p: any) => p.category === categoryId);
        const maxSrNo = categoryItems.length > 0
          ? Math.max(...categoryItems.map((p: any) => p.sr_no ?? 0), 0)
          : 0;
        sr_no = maxSrNo + 1;
      }

      // Pass the new item through calculateItemValues
      const newItem: SelectedRow = calculateItemValues({
        ...item,
        category: categoryId,
        tax_rate: Number(item.gst),
        gst: Number(item.gst),
        quantity: 1,
        discount: 0,
        discount_type: "%",
        original_selling_price: item.selling_price, // Store original price when item is first added
        brand: item.brand, // Preserve brand ID
        brand_name: item.brand_name, // Preserve brand name
        sr_no: sr_no, // Assign sr_no only for non-mandatory categories
      });

      return [...prev, newItem];
    });

    // Update dependencies after item selection
    const updateDependency = (key: keyof Dependencies, attrNames: string[]) => {
      for (const attrName of attrNames) {
        const attr = item.attributes.find((a) => a.attribute_name === attrName);
        if (attr) {
          setDependencies((prev) => ({
            ...prev,
            [key]: attr.manage_value_name,
          }));
          break; // Stop at first match
        }
      }
    };
    console.log(item);

    if (item.category === "743cffc9-09ae-4509-be91-314361e7bb33") {
      // CPU ID
      // updateDependency("cpu_motherboard", [
      //   "Processor-socket-type",
      //   "processor_socket_type",
      //   "Processor Socket Type",
      // ]);
      updateDependency("cpu_motherboard", ["Processor Socket Type"]);
    }

    // Motherboard category
    if (item.category === "14872373-f2f1-45ed-802e-81ef0971d0c6") {
      // Motherboard ID
      // updateDependency("motherboard_cabinet", [
      // "Motherboard form factor",
      // "motherboard_form_factor",
      //   "Motherboard Form Factor",
      // ]);
      // updateDependency("motherboard_memory", ["Ram Type", "ram_type", "RAM Type"]);
      updateDependency("motherboard_memory", ["RAM Type"]);
    }
  };

  const updateItemField = (
    item_id: string,
    categoryId: string,
    field: string,
    value: any
  ) => {
    setSelected((prev) =>
      prev.map((item) =>
        item.item_id === item_id && item.category === categoryId
          ? calculateItemValues({ ...item, [field]: value })
          : item
      )
    );
  };

  const removeItem = (itemId: string, categoryId: string) => {
    setSelected((prev) => {
      // Special handling for CPU deletion - also remove motherboard, memory and cabinet items
      if (categoryId === "743cffc9-09ae-4509-be91-314361e7bb33") {
        // CPU category ID - also remove motherboard, memory and cabinet
        const motherboardCategoryId = "14872373-f2f1-45ed-802e-81ef0971d0c6"; // Motherboard category ID
        const memoryCategoryId = "54f1c6b9-481a-4a16-a70e-bfe49341adf8"; // Memory category ID
        // const cabinetCategoryId = "e2ec7862-300c-4dcc-9fdb-650e5ddd8180"; // Cabinet category ID

        const filteredItems = prev.filter(
          (p) =>
            !(p.item_id === itemId && p.category === categoryId) && // Remove the CPU
            p.category !== motherboardCategoryId && // Remove all motherboard items
            p.category !== memoryCategoryId
          // && // Remove all memory items
          // p.category !== cabinetCategoryId // Remove all cabinet items
        );

        // Show toast notification if motherboard, memory or cabinet items were removed
        const removedMotherboardItems = prev.filter(
          (p) => p.category === motherboardCategoryId
        );
        const removedMemoryItems = prev.filter(
          (p) => p.category === memoryCategoryId
        );
        // const removedCabinetItems = prev.filter(
        //   (p) => p.category === cabinetCategoryId
        // );

        if (
          removedMotherboardItems.length > 0 ||
          removedMemoryItems.length > 0
          // ||
          // removedCabinetItems.length > 0
        ) {
          // Close all accordions
          setOpenCategoryId(null);
          toast.info(
            "CPU removed - motherboard, memory items have also been cleared"
            // "CPU removed - motherboard, memory, and cabinet items have also been cleared"
          );
        }

        return filteredItems;
      }

      // Special handling for motherboard deletion - also remove memory and cabinet items
      if (categoryId === "14872373-f2f1-45ed-802e-81ef0971d0c6") {
        // Motherboard category ID - also remove memory and cabinet
        const memoryCategoryId = "54f1c6b9-481a-4a16-a70e-bfe49341adf8"; // Memory category ID
        // const cabinetCategoryId = "e2ec7862-300c-4dcc-9fdb-650e5ddd8180"; // Cabinet category ID

        const filteredItems = prev.filter(
          (p) =>
            !(p.item_id === itemId && p.category === categoryId) && // Remove the motherboard
            p.category !== memoryCategoryId
          // && // Remove all memory items
          // p.category !== cabinetCategoryId // Remove all cabinet items
        );

        // Show toast notification if memory or cabinet items were removed
        const removedMemoryItems = prev.filter(
          (p) => p.category === memoryCategoryId
        );
        // const removedCabinetItems = prev.filter(
        //   (p) => p.category === cabinetCategoryId
        // );

        if (
          removedMemoryItems.length > 0
          // || removedCabinetItems.length > 0
        ) {
          // Close all accordions
          setOpenCategoryId(null);
          toast.info(
            "Motherboard removed - memory items have also been cleared"
            // "Motherboard removed - memory and cabinet items have also been cleared"
          );
        }

        return filteredItems;
      }

      // Normal removal for other categories
      return prev.filter(
        (p) => !(p.item_id === itemId && p.category === categoryId)
      );
    });
  };

  const setQty = (itemId: string, categoryId: string, qty: number) => {
    setSelected((prev) =>
      prev.map((p) =>
        p.item_id === itemId && p.category === categoryId
          ? calculateItemValues({
              ...p,
              quantity: Math.max(1, Math.min(10000, Math.floor(qty || 1))),
            })
          : p
      )
    );
  };

  const rowsByCategory = useMemo(() => {
    const map: Record<string, SelectedRow[]> = {};
    selected.forEach((r) => {
      map[r.category] = map[r.category] ? [...map[r.category], r] : [r];
    });
    
    // Sort items within non-mandatory categories by sr_no
    Object.keys(map).forEach((categoryId) => {
      const category = categories.find((c) => c.category_id === categoryId);
      if (category && !category.is_mandatory) {
        // Sort by sr_no for non-mandatory categories
        map[categoryId].sort((a, b) => {
          const srNoA = a.sr_no ?? Number.MAX_SAFE_INTEGER;
          const srNoB = b.sr_no ?? Number.MAX_SAFE_INTEGER;
          return srNoA - srNoB;
        });
      }
    });
    
    return map;
  }, [selected, categories]);

  // Update items to the parent
  useEffect(() => {
    const normalizeKey = (name: string) =>
      name
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "");

    const item_details: Record<string, any[]> = {};

    // Group items by category and sort non-mandatory category items by sr_no
    const itemsByCategory: Record<string, SelectedRow[]> = {};
    selected.forEach((it) => {
      if (!itemsByCategory[it.category]) {
        itemsByCategory[it.category] = [];
      }
      itemsByCategory[it.category].push(it);
    });

    // Sort items within each category
    Object.keys(itemsByCategory).forEach((categoryId) => {
      const category = categories.find((c) => c.category_id === categoryId);
      if (category && !category.is_mandatory) {
        // Sort by sr_no for non-mandatory categories
        itemsByCategory[categoryId].sort((a, b) => {
          const srNoA = a.sr_no ?? Number.MAX_SAFE_INTEGER;
          const srNoB = b.sr_no ?? Number.MAX_SAFE_INTEGER;
          return srNoA - srNoB;
        });
      }
    });

    // Build item_details with sorted items
    Object.keys(itemsByCategory).forEach((categoryId) => {
      itemsByCategory[categoryId].forEach((it) => {
        const payloadRow: any = {
          item_id: it.item_id,
          category: it.category,
          category_name: it.category_name,
          item_name: it.item_name,
          item_sku: it.item_sku,
          selling_price: Number(it.selling_price ?? 0).toFixed(2),
          gst: Number(it.gst ?? 0).toFixed(2),
          tax_rate: it.tax_rate,
          discount: it.discount,
          discount_type: it.discount_type,
          taxable_value: it.taxable_value,
          tax_amount: it.tax_amount,
          item_amount: it.item_amount,
          quantity: it.quantity,
          remarks: "",
          tags: null,
          tag_name: null,
          image: null,
          attributes: it.attributes,
          brand: it.brand, // Preserve brand ID
          brand_name: it.brand_name, // Preserve brand name
        };

        // Only include sr_no for non-mandatory categories
        const category = categories.find((c) => c.category_id === categoryId);
        if (category && !category.is_mandatory && it.sr_no !== undefined && it.sr_no !== null) {
          payloadRow.sr_no = it.sr_no;
        }

        const key = normalizeKey(it.category_name);

        if (!item_details[key]) {
          item_details[key] = []; // create only when needed
        }

        item_details[key].push(payloadRow);
      });
    });

    setItemList(item_details);
  }, [selected, categories]);

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(val);

  // Validation function to check mandatory categories
  const validateMandatoryCategories = React.useCallback(() => {
    if (categories.length === 0) return { isValid: true, errors: [] };

    const mandatoryCategories = categories.filter((cat) => cat.is_mandatory);
    const selectedCategoryIds = selected.map((item) => item.category);
    const missingCategories = mandatoryCategories.filter(
      (cat) =>
        !selectedCategoryIds.includes(cat.category_id) &&
        cat.category_name !== "Graphic Card"
    );

    if (missingCategories.length > 0) {
      const errors = missingCategories.map(
        (cat) =>
          `Please select at least one item from ${cat.category_name} category`
      );
      return { isValid: false, errors };
    }

    return { isValid: true, errors: [] };
  }, [categories, selected]);

  // Update validation state when selected items or categories change
  useEffect(() => {
    if (onValidationChange && categories.length > 0) {
      const validation = validateMandatoryCategories();
      onValidationChange(validation);
    }
  }, [selected, categories, onValidationChange, validateMandatoryCategories]);

  // Derive unlocked categories based on selection
  const unlockedCategories = useMemo(() => {
    if (categories.length === 0) return [];

    const first = categories[0]?.category_id;
    const second = categories[1]?.category_id;

    const selectedFirst = selected.some((s) => s.category === first);
    const selectedSecond = selected.some((s) => s.category === second);

    if (!selectedFirst) {
      // Only first unlocked
      return [first];
    } else if (!selectedSecond) {
      // First selected → unlock first + second
      return [first, second];
    } else {
      // First + second selected → unlock all
      return categories.map((c) => c.category_id);
    }
  }, [categories, selected]);

  //*// Auto open CPU
  // useEffect(() => {
  //   if (categories.length > 0 && !isEdit) {
  //     setOpenCategoryId(categories[0].category_id); // auto-open first row
  //   }
  // }, [categories]);

  console.log("selected items", selected);

  return (
    <div className="mb-3">
      <div className="bg-white mx-2">
        <form onSubmit={formik.handleSubmit} autoComplete="off">
          {categoriesLoading || isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              <div className="table-responsive table-card rounded-4 mx-0 m-lg-1 my-1 border">
                <Table className="table table-bordered table-centered align-middle table-custom-effect table-nowrap mb-0">
                  <thead
                    className="table-abgb"
                    style={{ backgroundColor: "#0070b6ff", color: "#fff" }}
                  >
                    <tr>
                      <th style={{ color: "#fff" }} className="px-3">
                        Category
                      </th>
                      {selected.length > 0 ? (
                        <>
                          <th style={{ color: "#fff" }} className="px-3">
                            Item Name
                          </th>
                          <th style={{ color: "#fff" }} className="px-3">
                            Brand
                          </th>
                          <th style={{ color: "#fff" }} className="px-3">
                            Item SKU
                          </th>
                          <th style={{ color: "#fff" }} className="px-3">
                            Selling Price
                          </th>
                          <th style={{ color: "#fff" }} className="px-3">
                            Quantity
                          </th>
                          <th style={{ color: "#fff" }} className="px-3">
                            Discount
                          </th>
                          {/* <th style={{ color: "#fff" }} className="px-3">
                            GST %
                          </th> */}
                          <th style={{ color: "#fff" }} className="px-3">
                            Total
                          </th>
                        </>
                      ) : (
                        <th
                          style={{ color: "#fff" }}
                          className="px-3"
                          colSpan={7}
                        >
                          Selection
                        </th>
                      )}
                      <th
                        style={{
                          color: "#fff",
                          width: 120,
                          position: "sticky",
                          right: 0,
                          backgroundColor: "#0070b6ff",
                          zIndex: 10,
                          borderLeft: "2px solid rgba(0, 0, 0, 0.1)",
                        }}
                        className="px-3"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Mandatory categories first */}
                    {categories
                      .filter((cat: Category) => cat.is_mandatory)
                      .map((cat) => {
                        const selectedRows =
                          rowsByCategory[cat.category_id] || [];
                        const isOpen = openCategoryId === cat.category_id;

                        return (
                          <React.Fragment key={cat.category_id}>
                            {selectedRows.length === 0 ? (
                              // When nothing is selected in this category
                              <tr>
                                {
                                  <td className="px-3">
                                    {formatLabel(cat.category_name)}
                                  </td>
                                }
                                <td colSpan={7} className="text-muted px-3">
                                  Choose{" "}
                                  {cat.category_name.includes("(")
                                    ? cat.category_name.split("(")[0].trim()
                                    : cat.category_name}
                                </td>
                                <td
                                  className="px-3"
                                  style={{
                                    position: "sticky",
                                    right: 0,
                                    backgroundColor: ACTIONS_COLUMN_BG_COLOR,
                                    zIndex: 9,
                                    borderLeft: "2px solid rgba(0, 0, 0, 0.1)",
                                  }}
                                >
                                  <Button
                                    variant="light"
                                    onClick={() => {
                                      if (
                                        unlockedCategories.includes(
                                          cat.category_id
                                        )
                                      ) {
                                        setOpenCategoryId(
                                          isOpen ? null : cat.category_id
                                        );
                                      }
                                    }}
                                    disabled={
                                      !unlockedCategories.includes(
                                        cat.category_id
                                      )
                                    }
                                    style={{
                                      backgroundColor:
                                        unlockedCategories.includes(
                                          cat.category_id
                                        )
                                          ? "#0070b6ff"
                                          : "#e41022",
                                      color: "white", // to make the icon visible
                                      border: "none",
                                      padding: "0px 8px",
                                    }}
                                  >
                                    <i
                                      className={
                                        openCategoryId === cat.category_id
                                          ? "bi bi-dash-lg"
                                          : "bi bi-plus"
                                      }
                                      style={{ fontSize: "1.5rem" }}
                                    />
                                  </Button>
                                </td>
                              </tr>
                            ) : (
                              // Render one row per selected item
                              selectedRows.map((item, idx) => {
                                // const total =
                                //   r.quantity * r.price * (1 + r.tax / 100);

                                const brandName = item.brand_name || "-";

                                return (
                                  <tr
                                    key={`${cat.category_id}-${item.item_id}`}
                                  >
                                    <td className="px-3">
                                      {idx === 0 && cat.category_name}
                                    </td>
                                    <td
                                      className="text-truncate"
                                      style={{ maxWidth: 300 }}
                                      title={item.item_name}
                                    >
                                      {item.item_name}
                                    </td>
                                    <td
                                      className="text-truncate"
                                      style={{ maxWidth: 150 }}
                                    >
                                      {brandName}
                                    </td>
                                    <td
                                      className="text-truncate"
                                      style={{ maxWidth: 250 }}
                                    >
                                      {item.item_sku}
                                    </td>
                                    <td style={{ maxWidth: 120 }}>
                                      <Form.Control
                                        type="number"
                                        value={item.selling_price}
                                        size="sm"
                                        className="form-control form-control-sm text-center"
                                        style={{
                                          width: "100px",
                                          height: "34px",
                                          padding: "0.25rem",
                                        }}
                                        min={
                                          item.original_selling_price ??
                                          item.selling_price
                                        }
                                        step="0.01"
                                        onChange={(e) => {
                                          let val = Number(e.target.value);

                                          // Handle empty input
                                          if (isNaN(val)) {
                                            return; // Don't update if invalid input
                                          }

                                          // Update the selling price (which will trigger recalculation)
                                          // Validation will happen on blur
                                          updateItemField(
                                            item.item_id,
                                            item.category,
                                            "selling_price",
                                            val
                                          );
                                        }}
                                        onBlur={(e) => {
                                          let val = Number(e.target.value);

                                          const originalPrice =
                                            item.original_selling_price ??
                                            item.selling_price;

                                          // Handle empty input
                                          if (isNaN(val) || !e.target.value) {
                                            // Reset to original selling_price if empty
                                            e.target.value =
                                              String(originalPrice);
                                            updateItemField(
                                              item.item_id,
                                              item.category,
                                              "selling_price",
                                              originalPrice
                                            );
                                            return;
                                          }

                                          // Ensure value is not less than original price
                                          if (val < originalPrice) {
                                            toast.error(
                                              `Selling price cannot be less than original price (${formatCurrency(
                                                originalPrice
                                              )})`
                                            );
                                            // Reset input to original selling_price
                                            e.target.value =
                                              String(originalPrice);
                                            updateItemField(
                                              item.item_id,
                                              item.category,
                                              "selling_price",
                                              originalPrice
                                            );
                                            return;
                                          }

                                          // Ensure value is positive
                                          if (val < 0) {
                                            toast.error(
                                              "Selling price cannot be negative"
                                            );
                                            // Reset input to original selling_price
                                            e.target.value =
                                              String(originalPrice);
                                            updateItemField(
                                              item.item_id,
                                              item.category,
                                              "selling_price",
                                              originalPrice
                                            );
                                            return;
                                          }
                                        }}
                                      />
                                    </td>

                                    <td style={{ maxWidth: 120 }}>
                                      <QuantityInput
                                        value={item.quantity}
                                        maxQty={10000}
                                        disabled={RESTRICTED_SINGLE_ITEM_CATEGORIES.some(
                                          (restricted) =>
                                            restricted.category_id ===
                                            cat.category_id
                                        )}
                                        onChange={(val) =>
                                          setQty(
                                            item.item_id,
                                            item.category,
                                            val
                                          )
                                        }
                                      />
                                    </td>

                                    {/* Discount column */}
                                    <td className="d-flex gap-2 align-items-center">
                                      <Form.Control
                                        type="number"
                                        value={item.discount}
                                        size="sm"
                                        className="form-control form-control-sm text-center"
                                        style={{
                                          width: "60px",
                                          height: "34px",
                                          padding: "0.25rem",
                                        }}
                                        onChange={(e) => {
                                          let val = Number(e.target.value);

                                          if (item.discount_type === "%") {
                                            // Clamp percentage discount between 0 and 100
                                            val = Math.max(
                                              0,
                                              Math.min(val, 100)
                                            );
                                          } else {
                                            // Flat discount cannot exceed total line price
                                            const maxFlatDiscount =
                                              item.selling_price *
                                              item.quantity;
                                            val = Math.max(
                                              0,
                                              Math.min(val, maxFlatDiscount)
                                            );
                                          }

                                          updateItemField(
                                            item.item_id,
                                            item.category,
                                            "discount",
                                            val
                                          );
                                        }}
                                      />
                                      <Form.Select
                                        value={item.discount_type}
                                        size="sm"
                                        onChange={(e) =>
                                          updateItemField(
                                            item.item_id,
                                            item.category,
                                            "discount_type",
                                            e.target.value
                                          )
                                        }
                                        className="text-center"
                                        style={{
                                          width: "45px",
                                          height: "34px",
                                          padding: 0, // remove internal padding completely
                                          lineHeight: "1", // align text vertically
                                          border: "1px solid #ced4da", // optional, keep consistent with row
                                          borderRadius: "0", // remove rounded corners if you want flush
                                          appearance: "none",
                                          WebkitAppearance: "none",
                                          MozAppearance: "none",
                                          // background: "transparent", // optional, avoid extra background
                                          background: `url("data:image/svg+xml;utf8,<svg fill='black' height='10' viewBox='0 0 24 24' width='10' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>") no-repeat right 4px center`,
                                          backgroundColor: "white",
                                          backgroundSize: "12px",
                                        }}
                                      >
                                        <option value="%">%</option>
                                        <option value="flat">₹</option>
                                      </Form.Select>
                                    </td>

                                    {/* Tax rate (GST) editable */}
                                    {/* <td style={{ maxWidth: 60 }}>
                                      <input
                                        type="number"
                                        value={item.gst}
                                        readOnly
                                        // onChange={(e) => {
                                        //   const val = Math.max(
                                        //     0,
                                        //     Math.min(
                                        //       Number(e.target.value),
                                        //       100
                                        //     )
                                        //   );
                                        //   updateItemField(
                                        //     item.item_id,
                                        //     item.category,
                                        //     "tax_rate",
                                        //     val
                                        //   );
                                        // }}
                                        className="form-control form-control-sm text-center bg-light"
                                      />
                                    </td> */}
                                    <td>{formatCurrency(item.item_amount)}</td>

                                    <td
                                      style={{
                                        position: "sticky",
                                        right: 0,
                                        backgroundColor:
                                          ACTIONS_COLUMN_BG_COLOR,
                                        zIndex: 9,
                                        borderLeft:
                                          "2px solid rgba(0, 0, 0, 0.1)",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "4px",
                                        }}
                                      >
                                        {/* Remove Button */}
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          style={{
                                            width: "30px",
                                            height: "30px",
                                            padding: 0,
                                          }}
                                          onClick={() =>
                                            removeItem(
                                              item.item_id,
                                              item.category
                                            )
                                          }
                                        >
                                          <i className="bi bi-trash" />
                                        </Button>

                                        {/* Accordion Button - only for first row of category */}
                                        {idx === 0 && (
                                          <Button
                                            size="sm"
                                            onClick={() => {
                                              if (
                                                unlockedCategories.includes(
                                                  cat.category_id
                                                )
                                              ) {
                                                setOpenCategoryId(
                                                  isOpen
                                                    ? null
                                                    : cat.category_id
                                                );
                                              }
                                            }}
                                            disabled={
                                              !unlockedCategories.includes(
                                                cat.category_id
                                              )
                                            }
                                            style={{
                                              width: "30px",
                                              height: "30px",
                                              padding: 0,
                                              backgroundColor:
                                                unlockedCategories.includes(
                                                  cat.category_id
                                                )
                                                  ? "#0070b6ff"
                                                  : "#e41022",
                                              color: "white",
                                              border: "none",
                                            }}
                                          >
                                            <i
                                              className={
                                                openCategoryId ===
                                                cat.category_id
                                                  ? "bi bi-dash-lg"
                                                  : "bi bi-plus"
                                              }
                                              style={{ fontSize: "1rem" }}
                                            />
                                          </Button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )}

                            {/* Accordion row for adding more items */}
                            <tr>
                              <td
                                colSpan={10}
                                style={{ border: 0, paddingTop: 0 }}
                              >
                                <Accordion activeKey={isOpen ? "0" : undefined}>
                                  <Accordion.Item eventKey="0">
                                    <Accordion.Body style={{ padding: 0 }}>
                                      <div
                                        style={{
                                          overflowX: "hidden",
                                          maxWidth: "100%",
                                          width: "100%",
                                        }}
                                      >
                                        <Suspense fallback={<LoadingCard />}>
                                          {isOpen && (
                                            <DynamicItems
                                              addItem={addItem}
                                              categoryId={cat.category_id}
                                              categoryName={cat.category_name}
                                              dependencies={dependencies}
                                              setDependencies={setDependencies}
                                            />
                                          )}
                                        </Suspense>
                                      </div>
                                    </Accordion.Body>
                                  </Accordion.Item>
                                </Accordion>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}

                    {/* Separator row */}
                    <tr>
                      <td
                        colSpan={10}
                        className="fw-bold text-center"
                        style={{
                          border: "none", // remove table borders
                          height: "100%", // allow full height
                          padding: "1rem 0", // give vertical space
                        }}
                      >
                        <div
                          className="bgback"
                          style={{
                            width: "100%",
                            padding: "0.75rem",
                            borderRadius: "10px",
                            alignContent: "left",
                          }}
                        >
                          Additional Items
                        </div>
                      </td>
                    </tr>

                    {/* Optional categories */}
                    {categories
                      .filter((cat: Category) => !cat.is_mandatory)
                      .map((cat) => {
                        const selectedRows =
                          rowsByCategory[cat.category_id] || [];
                        const isOpen = openCategoryId === cat.category_id;

                        return (
                          <React.Fragment key={cat.category_id}>
                            {selectedRows.length === 0 ? (
                              // When nothing is selected in this category
                              <tr>
                                {
                                  <td className="px-3">
                                    {formatLabel(cat.category_name)}
                                  </td>
                                }
                                <td colSpan={7} className="text-muted px-3">
                                  Choose{" "}
                                  {cat.category_name.includes("(")
                                    ? cat.category_name.split("(")[0].trim()
                                    : cat.category_name}
                                </td>
                                <td
                                  className="px-3"
                                  style={{
                                    position: "sticky",
                                    right: 0,
                                    backgroundColor: ACTIONS_COLUMN_BG_COLOR,
                                    zIndex: 9,
                                    borderLeft: "2px solid rgba(0, 0, 0, 0.1)",
                                  }}
                                >
                                  <Button
                                    variant="light"
                                    onClick={() => {
                                      if (
                                        unlockedCategories.includes(
                                          cat.category_id
                                        )
                                      ) {
                                        setOpenCategoryId(
                                          isOpen ? null : cat.category_id
                                        );
                                      }
                                    }}
                                    disabled={
                                      !unlockedCategories.includes(
                                        cat.category_id
                                      )
                                    }
                                    style={{
                                      backgroundColor:
                                        unlockedCategories.includes(
                                          cat.category_id
                                        )
                                          ? "#0070b6ff"
                                          : "#e41022",
                                      color: "white", // to make the icon visible
                                      border: "none",
                                      padding: "0px 8px",
                                    }}
                                  >
                                    <i
                                      className={
                                        openCategoryId === cat.category_id
                                          ? "bi bi-dash-lg"
                                          : "bi bi-plus"
                                      }
                                      style={{ fontSize: "1.5rem" }}
                                    />
                                  </Button>
                                </td>
                              </tr>
                            ) : (
                              // Render one row per selected item
                              selectedRows.map((item, idx) => {
                                // const total =
                                //   r.quantity * r.price * (1 + r.tax / 100);

                                const brandName =
                                  item.brand_name ||
                                  item.attributes.find(
                                    (attr: any) =>
                                      attr.attribute_name === "Brand" ||
                                      attr.attribute_name === "brand"
                                  )?.manage_value_name ||
                                  "-";

                                return (
                                  <tr
                                    key={`${cat.category_id}-${item.item_id}`}
                                  >
                                    <td className="px-3">
                                      {idx === 0 && cat.category_name}
                                    </td>
                                    <td
                                      className="text-truncate"
                                      style={{ maxWidth: 300 }}
                                      title={item.item_name}
                                    >
                                      {item.item_name}
                                    </td>
                                    <td
                                      className="text-truncate"
                                      style={{ maxWidth: 150 }}
                                    >
                                      {brandName}
                                    </td>
                                    <td
                                      className="text-truncate"
                                      style={{ maxWidth: 250 }}
                                    >
                                      {item.item_sku}
                                    </td>
                                    <td style={{ maxWidth: 120 }}>
                                      <Form.Control
                                        type="number"
                                        value={item.selling_price}
                                        size="sm"
                                        className="form-control form-control-sm text-center"
                                        style={{
                                          width: "100px",
                                          height: "34px",
                                          padding: "0.25rem",
                                        }}
                                        min={
                                          item.original_selling_price ??
                                          item.selling_price
                                        }
                                        step="0.01"
                                        onChange={(e) => {
                                          let val = Number(e.target.value);

                                          // Handle empty input
                                          if (isNaN(val)) {
                                            return; // Don't update if invalid input
                                          }

                                          // Update the selling price (which will trigger recalculation)
                                          // Validation will happen on blur
                                          updateItemField(
                                            item.item_id,
                                            item.category,
                                            "selling_price",
                                            val
                                          );
                                        }}
                                        onBlur={(e) => {
                                          let val = Number(e.target.value);

                                          const originalPrice =
                                            item.original_selling_price ??
                                            item.selling_price;

                                          // Handle empty input
                                          if (isNaN(val) || !e.target.value) {
                                            // Reset to original selling_price if empty
                                            e.target.value =
                                              String(originalPrice);
                                            updateItemField(
                                              item.item_id,
                                              item.category,
                                              "selling_price",
                                              originalPrice
                                            );
                                            return;
                                          }

                                          // Ensure value is not less than original price
                                          if (val < originalPrice) {
                                            toast.error(
                                              `Selling price cannot be less than original price (${formatCurrency(
                                                originalPrice
                                              )})`
                                            );
                                            // Reset input to original selling_price
                                            e.target.value =
                                              String(originalPrice);
                                            updateItemField(
                                              item.item_id,
                                              item.category,
                                              "selling_price",
                                              originalPrice
                                            );
                                            return;
                                          }

                                          // Ensure value is positive
                                          if (val < 0) {
                                            toast.error(
                                              "Selling price cannot be negative"
                                            );
                                            // Reset input to original selling_price
                                            e.target.value =
                                              String(originalPrice);
                                            updateItemField(
                                              item.item_id,
                                              item.category,
                                              "selling_price",
                                              originalPrice
                                            );
                                            return;
                                          }
                                        }}
                                      />
                                    </td>

                                    <td style={{ maxWidth: 120 }}>
                                      <QuantityInput
                                        value={item.quantity}
                                        maxQty={10000}
                                        disabled={RESTRICTED_SINGLE_ITEM_CATEGORIES.some(
                                          (restricted) =>
                                            restricted.category_id ===
                                            cat.category_id
                                        )}
                                        onChange={(val) =>
                                          setQty(
                                            item.item_id,
                                            item.category,
                                            val
                                          )
                                        }
                                      />
                                    </td>

                                    {/* Discount column */}
                                    <td className="d-flex gap-2 align-items-center">
                                      <Form.Control
                                        type="number"
                                        value={item.discount}
                                        size="sm"
                                        className="form-control form-control-sm text-center"
                                        style={{
                                          width: "60px",
                                          height: "34px",
                                          padding: "0.25rem",
                                        }}
                                        onChange={(e) => {
                                          let val = Number(e.target.value);

                                          if (item.discount_type === "%") {
                                            // Clamp percentage discount between 0 and 100
                                            val = Math.max(
                                              0,
                                              Math.min(val, 100)
                                            );
                                          } else {
                                            // Flat discount cannot exceed total line price
                                            const maxFlatDiscount =
                                              item.selling_price *
                                              item.quantity;
                                            val = Math.max(
                                              0,
                                              Math.min(val, maxFlatDiscount)
                                            );
                                          }

                                          updateItemField(
                                            item.item_id,
                                            item.category,
                                            "discount",
                                            val
                                          );
                                        }}
                                      />
                                      <Form.Select
                                        value={item.discount_type}
                                        size="sm"
                                        onChange={(e) =>
                                          updateItemField(
                                            item.item_id,
                                            item.category,
                                            "discount_type",
                                            e.target.value
                                          )
                                        }
                                        className="text-center"
                                        style={{
                                          width: "45px",
                                          height: "34px",
                                          padding: 0, // remove internal padding completely
                                          lineHeight: "1", // align text vertically
                                          border: "1px solid #ced4da", // optional, keep consistent with row
                                          borderRadius: "0", // remove rounded corners if you want flush
                                          appearance: "none",
                                          WebkitAppearance: "none",
                                          MozAppearance: "none",
                                          // background: "transparent", // optional, avoid extra background
                                          background: `url("data:image/svg+xml;utf8,<svg fill='black' height='10' viewBox='0 0 24 24' width='10' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>") no-repeat right 4px center`,
                                          backgroundColor: "white",
                                          backgroundSize: "12px",
                                        }}
                                      >
                                        <option value="%">%</option>
                                        <option value="flat">₹</option>
                                      </Form.Select>
                                    </td>

                                    {/* Tax rate (GST) editable */}
                                    {/* <td style={{ maxWidth: 60 }}>
                                      <input
                                        type="number"
                                        value={item.gst}
                                        readOnly
                                        // onChange={(e) => {
                                        //   const val = Math.max(
                                        //     0,
                                        //     Math.min(
                                        //       Number(e.target.value),
                                        //       100
                                        //     )
                                        //   );
                                        //   updateItemField(
                                        //     item.item_id,
                                        //     item.category,
                                        //     "tax_rate",
                                        //     val
                                        //   );
                                        // }}
                                        className="form-control form-control-sm text-center bg-light"
                                      />
                                    </td> */}
                                    <td>{formatCurrency(item.item_amount)}</td>

                                    <td
                                      style={{
                                        position: "sticky",
                                        right: 0,
                                        backgroundColor:
                                          ACTIONS_COLUMN_BG_COLOR,
                                        zIndex: 9,
                                        borderLeft:
                                          "2px solid rgba(0, 0, 0, 0.1)",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "4px",
                                        }}
                                      >
                                        {/* Remove Button */}
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          style={{
                                            width: "30px",
                                            height: "30px",
                                            padding: 0,
                                          }}
                                          onClick={() =>
                                            removeItem(
                                              item.item_id,
                                              item.category
                                            )
                                          }
                                        >
                                          <i className="bi bi-trash" />
                                        </Button>

                                        {/* Accordion Button - only for first row of category */}
                                        {idx === 0 && (
                                          <Button
                                            size="sm"
                                            onClick={() => {
                                              if (
                                                unlockedCategories.includes(
                                                  cat.category_id
                                                )
                                              ) {
                                                setOpenCategoryId(
                                                  isOpen
                                                    ? null
                                                    : cat.category_id
                                                );
                                              }
                                            }}
                                            disabled={
                                              !unlockedCategories.includes(
                                                cat.category_id
                                              )
                                            }
                                            style={{
                                              width: "30px",
                                              height: "30px",
                                              padding: 0,
                                              backgroundColor:
                                                unlockedCategories.includes(
                                                  cat.category_id
                                                )
                                                  ? "#0070b6ff"
                                                  : "#e41022",
                                              color: "white",
                                              border: "none",
                                            }}
                                          >
                                            <i
                                              className={
                                                openCategoryId ===
                                                cat.category_id
                                                  ? "bi bi-dash-lg"
                                                  : "bi bi-plus"
                                              }
                                              style={{ fontSize: "1rem" }}
                                            />
                                          </Button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )}

                            {/* Accordion row for adding more items */}
                            <tr>
                              <td
                                colSpan={10}
                                style={{ border: 0, paddingTop: 0 }}
                              >
                                <Accordion activeKey={isOpen ? "0" : undefined}>
                                  <Accordion.Item eventKey="0">
                                    <Accordion.Body style={{ padding: 0 }}>
                                      <div
                                        style={{
                                          overflowX: "hidden",
                                          maxWidth: "100%",
                                          width: "100%",
                                        }}
                                      >
                                        <Suspense fallback={<LoadingCard />}>
                                          {isOpen && (
                                            <DynamicItems
                                              addItem={addItem}
                                              categoryId={cat.category_id}
                                              categoryName={cat.category_name}
                                              dependencies={dependencies}
                                              setDependencies={setDependencies}
                                            />
                                          )}
                                        </Suspense>
                                      </div>
                                    </Accordion.Body>
                                  </Accordion.Item>
                                </Accordion>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default BuildYourPC;
